import { useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { countUnseen, useHelpStore } from '@/stores/help-store'
import { HelpMarkdown } from '@/components/help/HelpMarkdown'
import { MegaphoneIcon } from '@/components/help/MegaphoneIcon'

// Changelog entries carry a date-only `YYYY-MM-DD` value. Parsing that with
// `new Date()` yields UTC midnight, which renders as the previous day in
// negative-offset timezones — so build a local date from the parts instead.
function formatEntryDate(value: string): string {
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  const date = dateOnly
    ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
    : new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })
}

export function WhatsNewButton() {
  const { changelog, lastSeenAt, fetchChangelog, markChangelogSeen } = useHelpStore()
  const unseen = countUnseen(changelog, lastSeenAt)

  useEffect(() => {
    void fetchChangelog()
  }, [fetchChangelog])

  const handleOpenChange = (open: boolean) => {
    if (open && unseen > 0) void markChangelogSeen()
  }

  return (
    <Popover onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative h-8 w-8" aria-label="What's new">
          <MegaphoneIcon />
          {unseen > 0 && (
            <span
              data-testid="whats-new-dot"
              className="absolute right-0.5 top-0.5 h-2 w-2 rounded-full bg-[hsl(var(--ai-accent))]"
            />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between border-b px-4 py-3">
          <span className="text-sm font-medium">What's new</span>
        </div>
        <div className="max-h-96 overflow-y-auto">
          {changelog.length === 0 && (
            <p className="px-4 py-6 text-center text-sm text-muted-foreground">No updates yet.</p>
          )}
          {changelog.map((entry) => (
            <div key={entry.slug} className="border-b border-border/60 px-4 py-3 last:border-b-0">
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-sm font-medium">{entry.title}</span>
                <span className="flex-shrink-0 text-meta">{formatEntryDate(entry.date)}</span>
              </div>
              <div className="mt-1">
                <HelpMarkdown compact>{entry.body}</HelpMarkdown>
              </div>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  )
}
